import { inject, Injectable } from '@angular/core'
import { forkJoin, map, Observable, of, switchMap } from 'rxjs'
import { CoverageApiResponse, SubCoverageApiResponse } from '@features/insured/models/coverages/coverage-api.model'
import { CoverageService } from '@features/insured/services/coverages/coverage.service'
import { SubcoverageService } from '@features/insured/services/coverages/subcoverage.service'
import { hasActualValue } from '@core/utils/form-utils'
import { AviationHullApiResponse, AviationLiabilities, CommercialGeneralLiability } from '@features/insured/models/coverages/base-coverage.model'
import { CoverageMapperService } from '@features/insured/services/coverages/coverage-mapper.service'
import { COVERAGE_TYPE_ENUM } from '@shared/models/coverage-type.model'

/**
 * Orchestrates persisting a coverage and its subcoverages.
 *
 * BE contract:
 *  - POST/PUT /coverage   -> coverage WITHOUT subcoverages (subs are managed separately)
 *  - POST/PUT /subcoverage -> single subcoverage; for POST the `coverageUuid` must be set
 *
 * Strategy: save the parent coverage first (sequential), then forkJoin all sibling subcoverage
 * saves with the resulting `coverageUuid`.
 */
@Injectable({ providedIn: 'root' })
export class CoveragePersistenceService {
  private readonly coverageService = inject(CoverageService)
  private readonly subcoverageService = inject(SubcoverageService)
  private readonly mapper = inject(CoverageMapperService)

  /**
   * Persists the coverages built from the aviation hull and aviation liabilities forms for the
   * given policy. Coverages that have neither an existing uuid nor any subcoverages worth saving
   * are skipped.
   */
  public persistCoveragesForPolicy(
    policyId: string,
    hullForm: AviationHullApiResponse | null,
    liabilitiesForm: AviationLiabilities | null,
    commercialGeneralLiabilityForm: CommercialGeneralLiability | null = null
  ): Observable<CoverageApiResponse[]> {
    const drafts: CoverageApiResponse[] = []
    if (hullForm) drafts.push(this.mapper.mapAviationHullFormToCoverage(hullForm, policyId))
    if (liabilitiesForm) drafts.push(this.mapper.mapAviationLiabilitiesFormToCoverage(liabilitiesForm, policyId))
    if (commercialGeneralLiabilityForm) drafts.push(this.mapper.mapCommercialGeneralLiabilityFormToCoverage(commercialGeneralLiabilityForm, policyId))

    const persistable = drafts.filter(d => this.isWorthPersisting(d))
    if (persistable.length === 0) {
      return of([])
    }

    return forkJoin(persistable.map(d => this.persistCoverage(d)))
  }

  /**
   * Persists a single coverage:
   *   1. Save the coverage itself (POST when no uuid, PUT otherwise) without its subcoverages.
   *   2. With the resulting uuid, save every subcoverage in parallel (POST/PUT each).
   */
  public persistCoverage(draft: CoverageApiResponse): Observable<CoverageApiResponse> {
    const { subcoverages, ...coverageWithoutSubs } = draft
    const coveragePayload = { ...coverageWithoutSubs, subcoverages: [] } as CoverageApiResponse

    return this.coverageService.save(coveragePayload).pipe(
      switchMap(savedCoverage => this.persistSubcoverages(savedCoverage, subcoverages).pipe(
        map(savedSubs => ({ ...savedCoverage, subcoverages: savedSubs }))
      ))
    )
  }

  private persistSubcoverages(
    parent: CoverageApiResponse,
    subDrafts: SubCoverageApiResponse[]
  ): Observable<SubCoverageApiResponse[]> {
    if (subDrafts.length === 0) {
      return of([])
    }

    const requests = subDrafts.map(sub => this.subcoverageService.save({
      ...sub,
      coverageUuid: parent.uuid
    }))
    return forkJoin(requests)
  }

  private isWorthPersisting(coverage: CoverageApiResponse): boolean {
    if (hasActualValue(coverage.uuid)) return true
    if (coverage.subcoverages.length > 0) return true

    // Aviation Hull has parent-level booleans/flags that can be set independently
    if (coverage.coverageType === COVERAGE_TYPE_ENUM.AviationHull) {
      return hasActualValue(coverage.sparesEngineTestRun?.localValue)
        || hasActualValue(coverage.sparesJointlyFor?.localValue)
        || hasActualValue(coverage.sparesTwinInsured?.localValue)
        || hasActualValue(coverage.hullDeductibleJointlyFor?.localValue)
        || hasActualValue(coverage.hullDeductibleTwinInsured?.localValue)
    }
    return false
  }
}
